import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReservation } from '../../Common/Context/ReserveContextV2';
import { useAlert } from '../../Common/Context/AlertContext';
import CustomExtendAlert from './CustomExtendAlert';
import { SalesRootContext } from '../../Common/Context/SalesRootContext';
import ApiRequestV2 from '../../Common/hooks/ApiRequestV2';
import Spinner from '../../Common/components/Spinner'

const ReservationV2 = ({ selectedDateTime, onSuccess, resetSelectedDate }) => {
    const { isReserved, setIsReserved, tempReserveInfo, setTempReserveInfo } = useReservation();
    const [notifyTimeoutId, setNotifyTimeoutId] = useState(null);
    const [autoCancelTimeoutId, setAutoCancelTimeoutId] = useState(null);
    const { showAlert } = useAlert();
    const [showExtendAlert, setShowExtendAlert] = useState(false);
    const { salesRootData, setSalesRootData } = useContext(SalesRootContext);
    const { response, error, isLoading, fetchData } = ApiRequestV2();
    const [operation, setOperation] = useState(null);
    const navigate = useNavigate();

    const { company, selectedRoot: salesRoot, TLAP_id: TLAPId, TLAP_name: TLAPName, group } = salesRootData;

    // 延長アラートの「はい」ボタンが押されたときの処理
    const handleYes = () => {
        setShowExtendAlert(false);
        clearTimeout(autoCancelTimeoutId);
        setExtendAlertTimer();
        setAutoCancelTimer();
        showAlert('延長しました');
    };

    // 延長アラートの「いいえ」ボタンが押されたときの処理
    const handleNo = async () => {
        try {
            await autoCancelReservation();
            setIsReserved(false);
            setShowExtendAlert(false);
            clearTimeout(autoCancelTimeoutId);
            resetSelectedDate();
            showAlert('予約がキャンセルされました');
        } catch (error) {
            console.error('キャンセル処理中にエラーが発生しました', error);
        }
    };

    // 自動キャンセルの処理
    const autoCancelReservation = async () => {
        if (!isReserved) {
            clearTimers();
            return;
        }

        const body = {
            operation: 'cancel',
            company: company,
            TLAP_id: TLAPId,
            TLAP_name: TLAPName,
            group: group,
            sales_root: salesRoot,
            appointment_date: tempReserveInfo ? tempReserveInfo.temp_appoint_date : selectedDateTime,
        };

        try {
            const result = await fetchData('tlap/appointment/temp', body);
            if (result && result.result === 'success') {
                setIsReserved(false);
                setTempReserveInfo(null); // tempReserveInfoをクリア
                resetSelectedDate();
                showAlert('予約が自動キャンセルされました');
                setShowExtendAlert(false); // 延長アラートを非表示にする
            } else if (result && result.result === 'error') {
                console.error('自動キャンセルに失敗しました', result.message);
                showAlert('自動キャンセルに失敗しました');
                setShowExtendAlert(false); // エラーが発生した場合も延長アラートを非表示にする
            }
        } catch (error) {
            console.error('自動キャンセルに失敗しました', error);
            showAlert('自動キャンセルに失敗しました');
            setShowExtendAlert(false); // エラーが発生した場合も延長アラートを非表示にする
        }
    };


    // タイマーをクリアする関数
    const clearTimers = () => {
        clearTimeout(notifyTimeoutId);
        clearTimeout(autoCancelTimeoutId);
    };

    // 予約状態に応じてタイマーをセットまたはクリア
    useEffect(() => {
        if (isReserved) {
            setExtendAlertTimer();
            setAutoCancelTimer();
        } else {
            clearTimers();
        }

        return () => {
            clearTimers();
        };
    }, [isReserved]);

    // 延長アラートのタイマーを設定する関数
    const setExtendAlertTimer = () => {
        const notifyTimeout = setTimeout(() => {
            setShowExtendAlert(true);
        }, 1800000); // 30分
        setNotifyTimeoutId(notifyTimeout);
    };

    // 自動キャンセルのタイマーを設定する関数
    const setAutoCancelTimer = () => {
        const cancelTimeout = setTimeout(() => {
            autoCancelReservation();
        }, 2100000); // 35分
        setAutoCancelTimeoutId(cancelTimeout);
    };

    // 予約または予約取消の処理を行う関数
    const ReserveOrCancel = async () => {
        clearTimeout(notifyTimeoutId);
        clearTimeout(autoCancelTimeoutId);

        const operation = isReserved ? 'cancel' : 'reserve';
        setOperation(operation);
        const body = {
            operation: operation,
            company: company,
            TLAP_id: TLAPId,
            TLAP_name: TLAPName,
            group: group,
            sales_root: salesRoot,
            appointment_date: tempReserveInfo ? tempReserveInfo.temp_appoint_date : selectedDateTime,
        };

        const result = await fetchData('tlap/appointment/temp', body);
        if (result && result.result === 'success') {
            setIsReserved(!isReserved);
            if (!isReserved) {
                setExtendAlertTimer();
                setAutoCancelTimer();
            } else {
                clearTimers();
                setTempReserveInfo(null); // tempReserveInfoをクリア
                resetSelectedDate();
            }
            if (onSuccess) onSuccess(!isReserved, result);
        } else if (result && result.result === 'error') {
            if (result.message === '仮予約中です') {
                setIsReserved(true);
            }
            showAlert(result.message || 'エラーが発生しました');
        } else if (error) {
            console.error("APIリクエスト中にエラーが発生しました", error);
            showAlert('通信エラーが発生しました。');
        }
    };

    // 予約を確定する処理を行う関数
    const confirmReservation = async () => {
        const body = {
            company: company,
            group: group,
            sales_root: salesRoot,
            TLAP_id: TLAPId,
            TLAP_name: TLAPName,
            appointment_date: selectedDateTime,
        };

        const result = await fetchData('tlap/appointment/add', body);

        if (result) {
            if (result.result === "error") {
                showAlert(`予約の確定に失敗しました: ${result.message}`);
                console.error("予約の確定中にエラーが発生しました", result.message);
            } else {
                clearTimers();
                setIsReserved(true);
                if (onSuccess) onSuccess(true, result);

                // セールスルートデータの選択日付をリセット
                setSalesRootData(prevData => ({
                    ...prevData,
                    tempReserveInfo: null, // ここでtempReserveInfoだけをリセット
                }));

                resetSelectedDate(); // ここで選択された日付をリセットします

                navigate('/confirmed', {
                    state: {
                        ...result,
                        confirmedTime: new Date(selectedDateTime),
                        selectedRoot: salesRoot,
                        TLAP_id: TLAPId,
                        TLAP_name: TLAPName,
                        company: company,
                        group: group,
                    }
                });
                setIsReserved(false);
            }
        } else if (error) {
            showAlert(`予約の確定に失敗しました: ${error}`);
            console.error("予約の確定中にエラーが発生しました", error);
        }
    };

    // APIレスポンスやエラーのハンドリングを行うエフェクト
    useEffect(() => {
        if (response) {
            if (response.result === 'success') {
                if (operation === 'cancel') {
                    setIsReserved(false);
                    resetSelectedDate();
                    setShowExtendAlert(false);
                } else {
                    setIsReserved(true);
                }
            } else if (response.result === 'error') {
                if (response.message === '仮予約中です') {
                    setIsReserved(true);
                }
                showAlert(response.message || 'エラーが発生しました');
            }
            if (onSuccess) onSuccess(isReserved, response);
        } else if (error) {
            if (error.includes("empty_slot is 0")) {
                showAlert('空きがありませんでした');
            } else {
                showAlert('通信エラーが発生しました。');
                console.error("APIリクエスト中にエラーが発生しました", error);
            }
        }
    }, [response, error, operation]);

    return (
        <div className='reserve'>
            <div className='flexB'>
            {isLoading && <Spinner />}
                {isReserved ? (
                    <>
                        <button onClick={ReserveOrCancel} disabled={isLoading} className='reserve_btn buttonStyle'>
                            予約取消
                        </button>
                        <button onClick={confirmReservation} disabled={isLoading} className='comfBtn buttonStyle'>
                            予約登録
                        </button>
                    </>
                ) : (
                    <button onClick={ReserveOrCancel} disabled={isLoading} className='reserve_btn buttonStyle'>
                        予約
                    </button>
                )}
            </div>
          
            <CustomExtendAlert
                isOpen={showExtendAlert}
                message="予約がまもなく自動キャンセルされますが延長しますか？"
                onYes={handleYes}
                onNo={handleNo}
            />
        </div>
    );
};

export default ReservationV2;
